import React, { useCallback, useMemo } from 'react';

import type { WithdrawalAccount } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

import { SelectVirtualAccount } from './SelectVirtualAccount';

type Props = Omit<
  React.ComponentProps<typeof SelectVirtualAccount>,
  'accounts' | 'onChange' | 'onChangeCallback' | 'currentAccountNumber'
> & {
  accounts: WithdrawalAccount[];
  currentAccount?: WithdrawalAccount;
  onChangeCallback: (account: WithdrawalAccount) => void;
};

export const SelectWithdrawalAccount = ({
  accounts,
  currentAccount,
  onChangeCallback,
  ...props
}: Props) => {
  const virtualAccounts = useMemo<VirtualAccount[]>(
    () => accounts.map((account) => accountAdapter(account)),
    [accounts]
  );

  const onChange = useCallback(
    (key: string) => {
      const account = accounts.find(
        (account) => withdrawalAccountKey(account) === key
      )!;
      onChangeCallback(account);
    },
    [accounts, onChangeCallback]
  );

  return (
    <SelectVirtualAccount
      {...props}
      currentAccountNumber={
        currentAccount
          ? (withdrawalAccountKey(currentAccount) ?? undefined)
          : undefined
      }
      onChangeCallback={onChange}
      accounts={virtualAccounts}
    />
  );
};

const isCrypto = (account: WithdrawalAccount) =>
  account.local_instrument?.toLowerCase() === 'crypto';

const withdrawalAccountKey = (account: WithdrawalAccount) =>
  isCrypto(account)
    ? account.account_number
    : (account.iban ?? account.account_number);

const accountAdapter = (account: WithdrawalAccount): VirtualAccount =>
  ({
    currency_code: account.currency,
    account_number: withdrawalAccountKey(account),
    name: isCrypto(account) ? account.label : account.bank_name
  }) as VirtualAccount;
